import { getCustomTag, ModalRef, useEntityMetadataIncludeExternal, useTranslation } from '@msdk/common'
import {
  AllFormButtons,
  AutoFormButtons,
  ButtonAlign,
  CustomActionPositionToDivider,
  DefaultActionDetailsPositionToDivider,
  DefaultActionType,
  EntityAcl,
  EntityContext,
  EntityModel,
  EntityOperation,
  FormActionButtonType,
  FormMode,
  FormNode,
  ServerOperationResult,
} from '@msdk/definitions'
import { Divider } from '@msdk/ui'
import React from 'react'

import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { renderDefaultActions } from '../DynamicView/action-checks'
import { EnabledFormActions } from '../FormHeader/enable-form-actions'
import { ActionVisualizationMode } from './action-visualization'
import { ActionElement, CheckButtonComponent } from './ActionElement'
import { ActivateAction } from './Activate'
import { DeleteAction } from './Delete'
import { EditAction } from './EditAction'
import { FormActionButton } from './FormActionButton'
import { LogAction } from './LogAction'
import { MoveToBin } from './MoveToBin'

interface Props {
  entityName: string
  formName: string
  entity: EntityContext
  formNode: FormNode
  acl: EntityAcl
  invoker: CustomLogicInvoker
  navigate: (url: string, sitemap?: string, additionalCtx?: Record<string, string>) => void
  save: (actionId?: string) => Promise<EntityOperation>
  moveToBin: () => Promise<EntityOperation>
  restore: () => Promise<EntityOperation>
  delete: () => Promise<EntityOperation>
  reloadEntity: () => Promise<void>
  visualization?: ActionVisualizationMode
  mode: FormMode
  handleClose?: () => void
  enabledFormActions: EnabledFormActions
  autoFormButtons?: AutoFormButtons[]
  allFormButtons?: AllFormButtons[]
  modalRef?: ModalRef
  onModeChange?: (mode: FormMode, formName: string) => void
}

export function FormActions(props: Props) {
  const t = useTranslation()
  const model = useEntityMetadataIncludeExternal(props.entityName, props.formName) as EntityModel

  const visualization = props.visualization ?? 'Buttons'

  const handleMoveToBin = async () => {
    const canContinue = await props.invoker.onFormAction(props.entity, 'MOVE_TO_BIN')
    if (canContinue) {
      return await props.moveToBin()
    }
    return { result: ServerOperationResult.Failure } as EntityOperation
  }

  const handleDelete = async () => {
    const canContinue = await props.invoker.onFormAction(props.entity, 'DELETE')
    if (canContinue) {
      return await props.delete()
    }
    return { result: ServerOperationResult.Success } as EntityOperation
  }

  const handleRestore = async () => {
    const canContinue = await props.invoker.onFormAction(props.entity, 'RESTORE')
    if (canContinue) {
      return await props.restore()
    }
    return { result: ServerOperationResult.Failure } as EntityOperation
  }

  const customMenuActionsAbove = () =>
    props.enabledFormActions.customActions
      .filter((m) => m.positionToDivider === CustomActionPositionToDivider.Above && visualization === 'MenuItems')
      .map((m) => (
        <ActionElement
          disabled={m.action.isDisabled()}
          onClick={() => {
            m.action.onClick()
            props.handleClose && props.handleClose()
          }}
          key={m.name}
          testId={getCustomTag(['form', m.description, 'button'])}
          label={t(m.idml, m.description)}
          icon={m.icon}
          visualizationMode={visualization}
          aspect={m.aspect}
          iconAlign={m.iconAlign}
          tooltip={m.tooltip}
        />
      ))

  const customMenuActionsBelow = () =>
    props.enabledFormActions.customActions
      .filter((m) => m.positionToDivider === CustomActionPositionToDivider.Below && visualization === 'MenuItems')
      .map((m) => (
        <ActionElement
          disabled={m.action.isDisabled()}
          onClick={() => {
            m.action.onClick()
            props.handleClose && props.handleClose()
          }}
          key={m.name}
          testId={getCustomTag(['form', m.description, 'button'])}
          label={t(m.idml, m.description)}
          icon={m.icon}
          visualizationMode={visualization}
          aspect={m.aspect}
          iconAlign={m.iconAlign}
          tooltip={m.tooltip}
        />
      ))

  const actionsDefault = [
    {
      visible: props.enabledFormActions.edit,
      component: (
        <EditAction
          entity={props.entity}
          formName={props.formName}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Edit)}
          visualizationMode={visualization}
          modalRef={props.modalRef}
          onModeChange={props.onModeChange}
        />
      ),
      type: DefaultActionType.Edit,
    },
    {
      visible: props.enabledFormActions.activate,
      component: (
        <ActivateAction
          onRestore={handleRestore}
          visualizationMode={visualization}
          onClose={props.handleClose}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Activate)}
        />
      ),
      type: DefaultActionType.Activate,
    },
    {
      visible: props.enabledFormActions.delete,
      component: (
        <DeleteAction
          entityName={props.entityName}
          formName={props.formName}
          id={props.entity.id}
          onDelete={handleDelete}
          onClose={props.handleClose}
          visualizationMode={visualization}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Delete)}
        />
      ),
      type: DefaultActionType.Delete,
    },
    {
      visible: props.enabledFormActions.deactivate,
      component: (
        <MoveToBin
          onMoveToBin={handleMoveToBin}
          visualizationMode={visualization}
          onClose={props.handleClose}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Deactivate)}
        />
      ),
      type: DefaultActionType.Deactivate,
    },
  ]

  const defaultActions = () => renderDefaultActions(model.defaultActionsDetails, actionsDefault)
  const defaultMenuActionsAbove = () =>
    renderDefaultActions(
      model.defaultActionsDetails.filter((x) => x.positionToDivider === DefaultActionDetailsPositionToDivider.Above),
      actionsDefault,
    )
  const defaultMenuActionsBelow = () =>
    renderDefaultActions(
      model.defaultActionsDetails.filter((x) => x.positionToDivider === DefaultActionDetailsPositionToDivider.Below),
      actionsDefault,
    )

  const standardActions = () => {
    const actions = {
      edit: {
        visible: props.enabledFormActions.edit,
        component: (
          <EditAction
            entity={props.entity}
            formName={props.formName}
            actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Edit)}
            visualizationMode={visualization}
            modalRef={props.modalRef}
            onModeChange={props.onModeChange}
          />
        ),
        type: DefaultActionType.Edit,
      },
      activate: {
        visible: props.enabledFormActions.activate,
        component: (
          <ActivateAction
            onRestore={handleRestore}
            visualizationMode={visualization}
            onClose={props.handleClose}
            actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Activate)}
          />
        ),
        type: DefaultActionType.Activate,
      },
    }
    return Object.values(actions)
      .filter((action) => action.visible)
      .map((action) => action.component)
  }

  const deleteDeactivateActions = () => {
    const actions = {
      delete: {
        visible: props.enabledFormActions.delete,
        component: (
          <DeleteAction
            entityName={props.entityName}
            formName={props.formName}
            id={props.entity.id}
            onDelete={handleDelete}
            onClose={props.handleClose}
            visualizationMode={visualization}
            actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Delete)}
          />
        ),
        type: DefaultActionType.Delete,
      },
      deactivate: {
        visible: props.enabledFormActions.deactivate,
        component: (
          <MoveToBin
            onMoveToBin={handleMoveToBin}
            visualizationMode={visualization}
            onClose={props.handleClose}
            actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Deactivate)}
          />
        ),
        type: DefaultActionType.Deactivate,
      },
    }
    return Object.values(actions)
      .filter((action) => action.visible)
      .map((action) => action.component)
  }

  const actionsAbove = [
    ...standardActions().filter(
      (action) => !defaultActions().some((defaultAction) => defaultAction.type === action.type),
    ),
    ...defaultMenuActionsAbove(),
    ...customMenuActionsAbove(),
  ]
  const actionsBelow = [
    ...deleteDeactivateActions().filter(
      (action) => !defaultActions().some((defaultAction) => defaultAction.type === action.type),
    ),
    ...defaultMenuActionsBelow(),
    ...customMenuActionsBelow(),
  ]

  return (
    <>
      {visualization !== 'MenuItems' && props.enabledFormActions.edit && (
        <EditAction
          entity={props.entity}
          formName={props.formName}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Edit)}
          visualizationMode={visualization}
          modalRef={props.modalRef}
          onModeChange={props.onModeChange}
        />
      )}
      {visualization !== 'MenuItems' && props.enabledFormActions.activate && (
        <ActivateAction
          onRestore={handleRestore}
          visualizationMode={visualization}
          onClose={props.handleClose}
          actionDetails={model.defaultActionsDetails.find((e) => e.type === DefaultActionType.Activate)}
        />
      )}
      {visualization === 'SingleAction' &&
        props.enabledFormActions.customActions.map((m) => (
          <ActionElement
            disabled={m.action.isDisabled()}
            onClick={() => {
              m.action.onClick()
              props.handleClose && props.handleClose()
            }}
            key={m.name}
            testId={getCustomTag(['form', m.description, 'button'])}
            label={t(m.idml, m.description)}
            icon={m.icon}
            visualizationMode={visualization}
            aspect={m.aspect}
            iconAlign={m.iconAlign}
            tooltip={m.tooltip}
          />
        ))}
      {props.visualization === 'Buttons' &&
        props.allFormButtons?.map((button, i, array) => {
          const nextButton = array.length > i + 1 ? array[i + 1] : null
          const alignLeft =
            (nextButton?.alignment == ButtonAlign.Right && button.alignment == ButtonAlign.Left) ||
            (!nextButton && button.alignment == ButtonAlign.Left)
          if (button.type === FormActionButtonType.Custom) {
            const btn = props.enabledFormActions.customActions.find((b) => b.name === button.id)
            if (btn) {
              return (
                <ActionElement
                  disabled={btn.action.isDisabled()}
                  onClick={() => {
                    btn.action.onClick()
                    props.handleClose && props.handleClose()
                  }}
                  key={btn.name}
                  testId={getCustomTag(['form', btn.description, 'button'])}
                  label={t(btn.idml, btn.description)}
                  icon={btn.icon}
                  visualizationMode={visualization}
                  aspect={btn.aspect}
                  iconAlign={btn.iconAlign}
                  alignLeft={alignLeft}
                  tooltip={btn.tooltip}
                />
              )
            }
          } else if (button.type === FormActionButtonType.Auto) {
            const btn = props.autoFormButtons?.find((b) => b.id === button.id)
            if (btn) {
              const ButtonComponent = CheckButtonComponent(btn.aspect)
              return (
                <ButtonComponent
                  key={btn.id}
                  disabled={btn.disabled}
                  onClick={btn.onClick}
                  data-test-id={btn.testId}
                  alignLeft={alignLeft}
                >
                  {btn.label}
                </ButtonComponent>
              )
            }
          }
        })}
      {props.enabledFormActions.formActions.map((m) => (
        <FormActionButton
          key={m.name}
          action={m}
          entityId={props.entity.id}
          formNode={props.formNode}
          save={props.save}
          visualizationMode={visualization}
          handleClose={props.handleClose}
        />
      ))}

      {visualization === 'MenuItems' &&
        (props.enabledFormActions.log ||
          props.enabledFormActions.delete ||
          props.enabledFormActions.deactivate ||
          props.enabledFormActions.customActions) && (
          <>
            {actionsAbove}
            {actionsBelow.length > 0 && <Divider />}
            {props.enabledFormActions.log && (
              <LogAction entityName={props.entityName} visualizationMode={visualization} />
            )}
            {actionsBelow}
          </>
        )}
    </>
  )
}
